import { TFunction } from 'i18next';
import { MetricGroupConfig } from './actuality.types';

export const DEFAULT_GROUP_ORDER = 1000;

export const getMetricGroupsConfig = (t: TFunction) =>
  ({
    velocity: {
      id: 'velocity',
      order: 1,
      title: t('Velocity', { ns: 'feed' }),
      description: t(
        'Speed and efficiency metrics for movement and operations analysis',
        { ns: 'feed' },
      ),
      helpNavTo: 'kpi/velocity',
    },
    time: {
      id: 'time',
      order: 2,
      title: t('Time Analysis', { ns: 'feed' }),
      description: t(
        'Time-based metrics for duration, intervals, and temporal patterns',
        { ns: 'feed' },
      ),
      helpNavTo: 'kpi/time',
    },
    process: {
      id: 'process',
      order: 2,
      title: t('Process Analysis', { ns: 'feed' }),
      description: t(
        'Time-based metrics for duration, intervals, and temporal patterns',
        { ns: 'feed' },
      ),
      helpNavTo: 'kpi/process',
    },

    compliance: {
      id: 'compliance',
      order: 3,
      title: t('Compliance', { ns: 'feed' }),
      description: t(
        'Metrics tracking adherence to rules, standards, and operational procedures',
        { ns: 'feed' },
      ),
      helpNavTo: 'kpi/compliance',
    },
    proglove: {
      id: 'proglove',
      order: 6,
      title: t('Proglove', { ns: 'feed' }),
      description: t(
        'Metrics specific to Proglove device usage and performance',
        { ns: 'feed' },
      ),
      helpNavTo: 'kpi/proglove',
    },
    iot: {
      id: 'iot',
      order: 6,
      title: t('IoT', { ns: 'feed' }),
      description: t('Metrics specific to IoT device usage and performance', {
        ns: 'feed',
      }),
      helpNavTo: 'kpi/device',
    },
    scans: {
      id: 'scans',
      order: 7,
      title: t('Scanning Metrics', { ns: 'feed' }),
      description: t('Metrics related to scanning operations and efficiency', {
        ns: 'feed',
      }),
      helpNavTo: 'kpi/scans',
    },
    itemStats: {
      id: 'itemStats',
      order: 7,
      title: t('Item Statistics', { ns: 'feed' }),
      description: t(
        'Statistical metrics and analytics for item performance and handling',
        { ns: 'feed' },
      ),
      helpNavTo: 'kpi/item-stats',
    },
    itemUom: {
      id: 'itemUom',
      order: 8,
      title: t('Item (Base UOM)', { ns: 'feed' }),
      description: t('Metrics related to item base (min)', { ns: 'feed' }),
      helpNavTo: 'kpi/item-uom',
    },
    item: {
      id: 'item',
      order: 8,
      title: t('Item Metrics', { ns: 'feed' }),
      description: t(
        'General item-related metrics and performance indicators',
        {
          ns: 'feed',
        },
      ),
      helpNavTo: 'kpi/item',
    },
    inventory: {
      id: 'inventory',
      order: 9,
      title: t('Inventory Analytics', { ns: 'feed' }),
      description: t(
        'Metrics tracking inventory levels, movements, and stock management',
        { ns: 'feed' },
      ),
      helpNavTo: 'kpi/inventory',
    },
    orderStats: {
      id: 'orderStats',
      order: 10,
      title: t('Demand Profiling', { ns: 'feed' }),
      description: t(
        'Statistical metrics and analytics for Order (Demand) behaviour',
        { ns: 'feed' },
      ),
      helpNavTo: 'kpi/item-stats',
    },
    location: {
      id: 'location',
      order: 11,
      title: t('Location Analysis', { ns: 'feed' }),
      description: t(
        'Metrics analyzing spatial distribution and location utilization',
        { ns: 'feed' },
      ),
      helpNavTo: 'kpi/location',
    },
    others: {
      id: 'others',
      order: DEFAULT_GROUP_ORDER,
      title: t('Custom Metrics', { ns: 'feed' }),
      description: t(
        'Additional metrics and indicators not categorized in other groups',
        { ns: 'feed' },
      ),
      helpNavTo: 'kpi/others',
    },
  }) as const;

// Type for the group IDs
export type MetricGroupId = keyof ReturnType<typeof getMetricGroupsConfig>;

// Function to get the config with translations
export const getMetricGroupConfig = (
  t: TFunction,
): Record<MetricGroupId, MetricGroupConfig> => {
  const config = getMetricGroupsConfig(t);
  const result: Record<string, MetricGroupConfig> = {};

  for (const [id, groupConfig] of Object.entries(config)) {
    result[id] = {
      ...groupConfig,
      visible: true,
    };
  }

  return result as Record<MetricGroupId, MetricGroupConfig>;
};
